import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { miscApi } from '@/services/api';
import { trackSearch } from '@/utils/behaviorTracking';
import { useNavCategories } from '@/context/NavCategoriesContext';

export const SearchBar = ({
  initialSearch = '',
  initialCategory = '',
  initialCounty = '',
  variant = 'hero',
}) => {
  const { categories: navCategories } = useNavCategories();
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory || 'all');
  const [county, setCounty] = useState(initialCounty || 'all');
  const [counties, setCounties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setSearch(initialSearch);
    setCategory(initialCategory || 'all');
    setCounty(initialCounty || 'all');
  }, [initialSearch, initialCategory, initialCounty]);

  useEffect(() => {
    const fetchCounties = async () => {
      try {
        const response = await miscApi.getCounties();
        setCounties(response.data);
      } catch (error) {
        console.error('Error fetching counties:', error);
        setCounties([
          'Stockholm',
          'Uppsala',
          'Västra Götaland',
          'Skåne',
          'Östergötland',
          'Jönköping',
          'Halland',
          'Örebro',
          'Dalarna',
          'Norrbotten',
        ]);
      }
    };
    fetchCounties();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    if (search) {
      trackSearch(search);
    }

    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category && category !== 'all') params.set('category', category);
    if (county && county !== 'all') params.set('county', county);
    navigate(`/produkter?${params.toString()}`);
  };

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSearch} className="flex gap-2" data-testid="search-bar-compact">
        <Input
          type="text"
          placeholder="Sök byggvaror..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10"
          data-testid="search-input-compact"
        />
        <Button type="submit" size="sm" className="bg-[#FF8A00] hover:bg-[#e67c00]" data-testid="search-btn-compact">
          <Search className="h-4 w-4" />
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSearch} className="w-full" data-testid="search-bar-hero">
      <div className="bg-white rounded-md shadow-floating p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sök byggfynd</label>
            <Input
              type="text"
              placeholder="T.ex. reglar, kakel, färg..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 rounded-sm"
              data-testid="search-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Kategori</label>
            <Select value={category || 'all'} onValueChange={setCategory}>
              <SelectTrigger className="h-12 rounded-sm" data-testid="category-select">
                <SelectValue placeholder="Alla kategorier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla kategorier</SelectItem>
                {navCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Län</label>
            <Select value={county || 'all'} onValueChange={setCounty}>
              <SelectTrigger className="h-12 rounded-sm" data-testid="county-select">
                <SelectValue placeholder="Alla län" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla län</SelectItem>
                {counties.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              className="w-full h-12 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold rounded-sm"
              data-testid="search-btn"
            >
              <Search className="h-5 w-5 mr-2" />
              Sök
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
